import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardActionArea, CardContent, Chip, CircularProgress, Collapse, Stack, TextField, Typography } from '@mui/material';
import { v2SettingsApi, type CatalogEntry } from './v2-settings-api';

export const CatalogExplorer: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<Array<[string, CatalogEntry]>>([]);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await v2SettingsApi.getCatalog();
        if (cancelled) return;
        setEntries(Object.entries(r?.settings ?? {}).sort(([a], [b]) => a.localeCompare(b)));
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.message || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!filter) return entries;
    const q = filter.toLowerCase();
    return entries.filter(([name, def]) =>
      name.toLowerCase().includes(q) ||
      def.description.toLowerCase().includes(q) ||
      def.legacyAliases.some((a) => a.toLowerCase().includes(q)),
    );
  }, [entries, filter]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error)   return <Typography color="error" sx={{ p: 4 }}>Error: {error}</Typography>;

  return (
    <Box>
      <TextField
        fullWidth
        placeholder="Filter by name, description, or legacy alias"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        sx={{ mb: 2 }}
        size="small"
      />
      <Typography variant="caption" sx={{ mb: 2, display: 'block' }}>
        {filtered.length} of {entries.length} settings
      </Typography>
      <Stack spacing={2}>
        {filtered.map(([name, def]) => {
          const isOpen = expanded.has(name);
          return (
            <Card key={name} variant="outlined">
              <CardActionArea onClick={() => toggle(name)}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{name}</Typography>
                    <Chip size="small" label={def.type} color="primary" />
                    {def.lockable && <Chip size="small" label="lockable" />}
                    {def.pendingReview && <Chip size="small" label="pendingReview" color="warning" />}
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {isOpen ? 'hide JSON' : 'show JSON'}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mb: 1 }}>{def.description}</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>levels:</Typography>
                    {def.allowedLevels.map((l) => (
                      <Chip key={l} size="small" label={l} variant="outlined" />
                    ))}
                  </Stack>
                  {def.enum && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>enum:</Typography>
                      {def.enum.map((v) => <Chip key={v} size="small" label={v} />)}
                    </Stack>
                  )}
                  {(def.min !== undefined || def.max !== undefined) && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      range: [{def.min ?? '−∞'}, {def.max ?? '+∞'}]
                    </Typography>
                  )}
                  {def.default !== undefined && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      default: <code>{JSON.stringify(def.default)}</code>
                    </Typography>
                  )}
                  {def.legacyAliases.length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                      legacy aliases: {def.legacyAliases.join(', ')}
                    </Typography>
                  )}
                  {def.migrationNote && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', mt: 0.5 }}>
                      migration: {def.migrationNote}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
              <Collapse in={isOpen} unmountOnExit>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 2,
                      bgcolor: 'grey.100',
                      border: 1,
                      borderColor: 'grey.300',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      overflowX: 'auto',
                    }}
                  >
                    {JSON.stringify({ [name]: def }, null, 2)}
                  </Box>
                </Box>
              </Collapse>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};
